import { openDB, type IDBPDatabase } from 'idb';

/**
 * Offline Mutation Queue
 *
 * Persists mutations in IndexedDB when the client is offline. When
 * connectivity is restored they are replayed in FIFO order.
 *
 * There is exactly one mutation kind, `'supabase'`: `{ table, op, payload,
 * matchId }`, replayed through the typed Supabase client under the restored
 * authenticated session so Row Level Security applies to the write. The album,
 * calendar, todo and notification services are the only producers.
 *
 * This module imports `idb` and nothing else. It does not know about HTTP, and
 * it has never had a second transport — an earlier revision declared an `'api'`
 * kind "for entries persisted before the Supabase migration", but that build
 * never existed: this file and the `madivladi-offline` database were both
 * created two weeks AFTER Supabase was integrated, and the only entry point
 * that could have written such an entry had no caller on the commit that
 * introduced it or on any commit since. The kind was removed rather than
 * documented; {@link checkReplayable} is what the read path does instead.
 *
 * ## Storage is a trust boundary
 *
 * IndexedDB outlives the bundle that wrote it, and by design: `vite.config.ts`
 * sets `registerType: 'prompt'` with no `skipWaiting`/`clientsClaim`, so a
 * client that never accepts an update keeps serving the old service worker's
 * precached bundle indefinitely. An entry read back here may therefore have
 * been serialised months ago, by a build whose column set the database no
 * longer has. A read is validated, never trusted:
 *   - {@link checkReplayable} re-derives the shape and checks the table against
 *     {@link REPLAYABLE_TABLES} before anything is issued.
 *   - {@link stripLegacyFields} drops columns a since-applied migration
 *     removed, so an old payload does not fail against a new schema.
 * An entry that fails the check is DEAD-LETTERED and reported, never dropped:
 * see "Replay semantics" below and {@link ReplaySummary.failed}.
 *
 * ## Replay semantics
 *
 * A replay step reports a {@link ReplayOutcome} rather than a boolean, and the
 * queue treats the three outcomes differently:
 *   - `'success'`   — the entry is deleted and counted as synced.
 *   - `'retryable'` — a wire failure or a 5xx. The entry keeps its place, its
 *     `attempts` counter is incremented and `nextAttemptAt` is pushed out by an
 *     exponential backoff. The run stops there so the entries behind it cannot
 *     overtake it — FIFO ordering is preserved for anything merely waiting.
 *   - `'permanent'` — an RLS refusal, a constraint violation, or an
 *     UPDATE/DELETE that matched zero rows. Retrying can never succeed, so the
 *     entry is moved to the dead-letter store and the run *continues*: a poison
 *     message must not block every later write forever.
 *
 * An entry that exhausts {@link MAX_REPLAY_ATTEMPTS} *and*
 * {@link MAX_REPLAY_AGE_MS} is dead-lettered on the same path. Both are
 * required, because the attempt cap on its own is a 76-second stopwatch and a
 * mobile radio takes longer than that to come back. Nothing is silently dropped
 * — the caller receives every evicted entry in {@link ReplaySummary.failed} and
 * is expected to report it.
 *
 * ## Change notification
 *
 * Every mutation of the queue (enqueue, remove, clear, dead-letter, and each
 * replay-phase transition) notifies {@link subscribeQueueChange} listeners and
 * keeps {@link getCachedPendingCount} in sync. That cached count is what makes
 * the badge live and what lets the `online` handler below decide, synchronously
 * and without touching IndexedDB, whether a replay is about to happen.
 *
 * ## The `online` arming window
 *
 * {@link isReplaying} flips to `true` on the window `online` event itself — not
 * when the React effect later gets around to calling {@link replayMutations}.
 * A consumer that refetches on `online` (`useRefetchOnFocus`) reads it from its
 * own `online` handler; if the arming only happened once replay had actually
 * started, that consumer would observe a false "idle", refetch first, and wipe
 * the not-yet-replayed rows off screen. The arming is released by a replay run
 * finishing, or by {@link REPLAY_ARM_TIMEOUT_MS} elapsing with no run having
 * started (nothing is mounted to drive one), so a waiter can never be stranded.
 *
 * Note what "idle" is NOT: a run that stops on a retryable failure goes back to
 * idle with the entry still queued. A consumer that must not overwrite queued
 * rows therefore waits on `isReplaying() === false` AND
 * {@link getCachedPendingCount} reaching zero — {@link waitForReplayIdle} alone
 * answers a narrower question, and is kept for callers that only care whether a
 * run is in flight.
 *
 * Integrates with:
 * - useOfflineQueue hook (replay dispatch, sync state, failure reporting)
 * - useOnlineStatus hook (connectivity events)
 * - useRefetchOnFocus hook (defers the online refetch until replay is idle)
 * - Header UI (pending sync badge)
 */

const DB_NAME = 'madivladi-offline';
const DB_VERSION = 2;
const STORE_NAME = 'pending-mutations';
const FAILED_STORE_NAME = 'failed-mutations';

/**
 * Replay attempts an entry gets before it is treated as a poison message and
 * moved to the dead-letter store.
 *
 * NOT sufficient on its own — see {@link MAX_REPLAY_AGE_MS}. Both have to be
 * exhausted before a retryable failure is evicted.
 */
export const MAX_REPLAY_ATTEMPTS = 5;

/**
 * How long an entry that keeps failing TRANSIENTLY stays in the live queue
 * before {@link MAX_REPLAY_ATTEMPTS} is allowed to evict it.
 *
 * The attempt cap alone is not a poison-message test, it is a stopwatch: five
 * attempts at 5 s / 10 s / 20 s / 40 s of backoff is about 76 seconds, and the
 * commonest real-world "offline" — a radio that has re-associated but cannot
 * complete a request yet — lasts longer than that. Spending the cap on it
 * dead-lettered the head of the queue, then the next entry, then the next, so a
 * five-minute dead uplink destroyed three or four queued writes that a later
 * attempt would have landed intact.
 *
 * So a retryable outcome must exhaust BOTH: the attempt count and this wall
 * clock, measured from when the entry was queued. A genuinely poisoned entry
 * still leaves the live queue, just a day later instead of a minute later. A
 * PERMANENT outcome is unaffected and is dead-lettered on the first answer —
 * that is the case where retrying can never help.
 */
export const MAX_REPLAY_AGE_MS = 24 * 60 * 60_000;

/**
 * How long {@link getDb} waits for the database to open before rejecting.
 *
 * An `openDB` that needs a version upgrade stays PENDING FOREVER while another
 * tab holds a connection at the old version, and a tab running a build that
 * predates the `blocking()` handler below never lets go on its own. Every queue
 * operation awaits this promise, so without a bound an enqueue simply never
 * settles: the service call never returns, the store's `.catch()` never fires,
 * nothing rolls back, nothing is persisted, and the optimistic row is lost on
 * the next reload with no error anywhere. Rejecting is strictly better —
 * the caller already handles a failed write.
 */
const DB_OPEN_TIMEOUT_MS = 10_000;

/** First backoff step; doubles per attempt up to {@link BACKOFF_MAX_MS}. */
const BACKOFF_BASE_MS = 5_000;

/** Ceiling for the exponential backoff between replay attempts. */
const BACKOFF_MAX_MS = 5 * 60_000;

/**
 * How long an `online`-armed replay stays armed without a run starting.
 * Nothing may be mounted to drive one (the hook lives in the Header), so the
 * arming self-releases rather than stranding {@link waitForReplayIdle} waiters.
 */
export const REPLAY_ARM_TIMEOUT_MS = 15_000;

/** Fields shared by every queued mutation. */
interface BasePendingMutation {
  readonly id?: number;
  readonly timestamp: number;
  readonly description: string;
  /**
   * Failed replay attempts already spent. Absent on entries written by a build
   * older than `DB_VERSION` 2 — read as `0` everywhere.
   */
  readonly attempts?: number;
  /** Message from the most recent failed replay attempt. */
  readonly lastError?: string;
  /** Epoch ms before which this entry must not be replayed again (backoff). */
  readonly nextAttemptAt?: number;
}

/** Supabase write operation. */
export type SupabaseMutationOp = 'insert' | 'update' | 'delete';

/** Mutation replayed through the typed Supabase client. */
export interface SupabasePendingMutation extends BasePendingMutation {
  readonly kind: 'supabase';
  /** Target table in the `public` schema. */
  readonly table: string;
  readonly op: SupabaseMutationOp;
  /** Row payload for insert/update. */
  readonly payload?: Record<string, unknown>;
  /** Primary key used to scope update/delete (`.eq('id', matchId)`). */
  readonly matchId?: string;
}

/**
 * An entry AS IT COMES BACK OUT OF STORAGE.
 *
 * Deliberately NOT {@link SupabasePendingMutation}: everything past the base
 * metadata is whatever some bundle serialised, so it is `unknown` until
 * {@link checkReplayable} has looked at it. The declared value type on the
 * object store is a statement about what THIS build writes, not a guarantee
 * about what it will read — the store predates this build and will outlive it.
 *
 * Nothing narrows implicitly. `replayMutations` moves these through the queue
 * without ever reading `table`, `op` or `payload`; the replay function is the
 * one place that has to know, and it has to earn the narrowing.
 */
export interface PendingMutation extends BasePendingMutation {
  readonly kind?: string;
  readonly table?: unknown;
  readonly op?: unknown;
  readonly payload?: unknown;
  readonly matchId?: unknown;
}

/**
 * A mutation that will never be replayed again, kept for inspection instead of
 * being dropped. Lives in its own object store so it cannot block the live
 * queue's FIFO ordering.
 */
export interface FailedMutation {
  readonly id?: number;
  /** Epoch ms at which the entry was evicted from the live queue. */
  readonly failedAt: number;
  /** Why it was evicted — the last replay error, or the attempt cap. */
  readonly reason: string;
  /** The entry as it stood when it was evicted, attempt counters included. */
  readonly mutation: PendingMutation;
}

/** Fields the queue owns; a caller never supplies them. */
type ReplayMetadata = Pick<
  BasePendingMutation,
  'id' | 'timestamp' | 'attempts' | 'lastError' | 'nextAttemptAt'
>;

/* -------------------------------------------------------------------------- */
/* Read-back validation                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Every table this app enqueues a write for, and the only tables a replay is
 * allowed to issue against.
 *
 * The four producing services are the whole list: `supabaseAlbumService`
 * (`albums`, `photos`), `supabaseCalendarService` (`events`),
 * `supabaseTodoService` (`tasks`) and `supabaseNotificationService`
 * (`notifications`).
 *
 * The allowlist exists because a replay builds its request out of a STRING that
 * came back from IndexedDB — `client.from(entry.table)` — and RLS is not a
 * substitute for it. RLS decides who may write a row; it says nothing about
 * whether `table` is a table this app was ever supposed to touch. A corrupted
 * record, a hand-edited store, or an entry written by a build that has since
 * renamed a table would otherwise be issued as an arbitrary PostgREST write
 * under the user's own session, and the only thing standing in its way would be
 * whatever policy happens to be on the table it names.
 *
 * Adding a table here is a deliberate act: it must be a table one of the
 * services actually enqueues.
 */
export const REPLAYABLE_TABLES: ReadonlySet<string> = new Set([
  'albums',
  'photos',
  'events',
  'tasks',
  'notifications',
]);

/** `satisfies` so this list cannot drift from {@link SupabaseMutationOp}. */
const REPLAYABLE_OPS: ReadonlySet<string> = new Set<string>([
  'insert',
  'update',
  'delete',
] satisfies SupabaseMutationOp[]);

/** Outcome of validating one entry read back from storage. */
export type ReplayableCheck =
  | { readonly ok: true; readonly mutation: SupabasePendingMutation }
  | { readonly ok: false; readonly reason: string };

/** A payload has to be a plain object before it can be sent as a row. */
function isRowPayload(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Decide whether a stored entry may be replayed, and narrow it if it may.
 *
 * ## Why this returns a REASON rather than a boolean
 *
 * An entry that fails here is not discarded. The caller turns the reason into a
 * `'permanent'` {@link ReplayOutcome}, which routes the entry to the
 * dead-letter store and into {@link ReplaySummary.failed}, where the hook
 * reports it with a destructive toast and a Sentry event. That is the same
 * treatment an RLS refusal gets, and it is deliberate: this queue exists
 * because writes were being lost silently, and "the shape looked wrong so we
 * dropped it" is the same bug wearing a different hat. The user is told that a
 * change of theirs could not be saved, and the record is still on disk to be
 * inspected.
 *
 * ## What this replaces
 *
 * Nothing used to check. The storage read cast the array (`as
 * PendingMutation[]`), the dispatcher tested `kind === 'supabase'` and sent
 * everything else down a REST path, and the replay then handed
 * `entry.table`/`entry.payload` straight to PostgREST. So the strongest
 * statement that could be made about an issued write was "some build put this
 * in IndexedDB". This function is that statement's replacement.
 */
export function checkReplayable(entry: PendingMutation): ReplayableCheck {
  if (entry.kind !== 'supabase') {
    const kind = entry.kind === undefined ? 'none' : `"${entry.kind}"`;
    return {
      ok: false,
      reason: `Queued entry has an unsupported mutation kind (${kind}); this build only replays Supabase writes`,
    };
  }
  if (typeof entry.table !== 'string' || entry.table === '') {
    return { ok: false, reason: 'Queued entry names no target table' };
  }
  if (!REPLAYABLE_TABLES.has(entry.table)) {
    return {
      ok: false,
      reason: `Queued entry targets "${entry.table}", which is not a table this app enqueues writes for`,
    };
  }
  if (typeof entry.op !== 'string' || !REPLAYABLE_OPS.has(entry.op)) {
    return {
      ok: false,
      reason: `Queued entry on "${entry.table}" has an unknown operation`,
    };
  }
  if (entry.payload !== undefined && !isRowPayload(entry.payload)) {
    return {
      ok: false,
      reason: `Queued ${entry.op} on "${entry.table}" carries a payload that is not a row object`,
    };
  }
  if (entry.matchId !== undefined && typeof entry.matchId !== 'string') {
    return {
      ok: false,
      reason: `Queued ${entry.op} on "${entry.table}" has a non-string target id`,
    };
  }
  // Earned, not assumed: the checks above are exactly the fields
  // `SupabasePendingMutation` declares, in the types it declares them in.
  return { ok: true, mutation: entry as SupabasePendingMutation };
}

/** Type guard: is this entry a replayable Supabase mutation? */
export function isSupabaseMutation(
  mutation: PendingMutation
): mutation is SupabasePendingMutation {
  return checkReplayable(mutation).ok;
}

/**
 * Columns a queued payload may still name that the live schema no longer has,
 * keyed by table.
 *
 * A queue entry is a serialised row from the bundle that wrote it, and that
 * bundle can be arbitrarily old (see "Storage is a trust boundary" above). When
 * a migration drops a column, every payload already sitting in IndexedDB keeps
 * naming it, and PostgREST answers such a write with `PGRST204` — which carries
 * no SQLSTATE, so `classifyPostgrestError` reads it as RETRYABLE. The entry then
 * burns {@link MAX_REPLAY_ATTEMPTS} and {@link MAX_REPLAY_AGE_MS} — five
 * attempts across a day — before being dead-lettered, and the user's change is
 * lost anyway, just slowly.
 *
 * `photos.thumbnail_url` is the live case: migration 024 made it optional and
 * pending migration 025 drops it, deliberately in a second push AFTER the
 * bundle that stopped writing it is deployed. Every row this app ever wrote
 * stored the same object key in `url` and `thumbnail_url`, so dropping the
 * field from a queued payload discards a duplicate and nothing else — and it is
 * correct on BOTH sides of 025, because 024 left the column nullable with a
 * `''` default.
 *
 * Entries stay here after their migration has landed. The bundle that wrote
 * them is what dates them, not the migration.
 */
export const LEGACY_PAYLOAD_FIELDS: Readonly<
  Record<string, readonly string[]>
> = {
  photos: ['thumbnail_url'],
};

/**
 * Remove columns {@link LEGACY_PAYLOAD_FIELDS} says the schema no longer has.
 *
 * Returns the payload unchanged (same reference) when there is nothing to
 * strip, so the ordinary case allocates nothing.
 */
export function stripLegacyFields(
  table: string,
  payload: Record<string, unknown>
): Record<string, unknown> {
  const legacy = LEGACY_PAYLOAD_FIELDS[table];
  if (legacy === undefined) return payload;

  const present = legacy.filter((field) => field in payload);
  if (present.length === 0) return payload;

  const stripped: Record<string, unknown> = { ...payload };
  for (const field of present) {
    delete stripped[field];
  }
  return stripped;
}

/**
 * Persistence seam for the queue.
 *
 * The default implementation is IndexedDB. It is an interface so the replay
 * logic — ordering, attempt counting, backoff, poison eviction — can be
 * exercised by tests in an environment that has no IndexedDB at all.
 */
export interface OfflineQueueStorage {
  readonly add: (entry: PendingMutation) => Promise<number>;
  readonly put: (entry: PendingMutation) => Promise<void>;
  readonly getAll: () => Promise<PendingMutation[]>;
  readonly count: () => Promise<number>;
  readonly remove: (id: number) => Promise<void>;
  readonly clear: () => Promise<void>;
  readonly addFailed: (entry: FailedMutation) => Promise<void>;
  readonly getAllFailed: () => Promise<FailedMutation[]>;
  readonly clearFailed: () => Promise<void>;
}

interface OfflineDb {
  [STORE_NAME]: {
    key: number;
    value: PendingMutation;
  };
  [FAILED_STORE_NAME]: {
    key: number;
    value: FailedMutation;
  };
}

let dbPromise: Promise<IDBPDatabase<OfflineDb>> | null = null;

/** Reject rather than hang when the open is blocked; see {@link DB_OPEN_TIMEOUT_MS}. */
async function withOpenTimeout(
  opening: Promise<IDBPDatabase<OfflineDb>>
): Promise<IDBPDatabase<OfflineDb>> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      opening,
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(() => {
          reject(
            new Error(
              'offlineMutationQueue: IndexedDB did not open — another tab is ' +
                'holding an older version of the database open. Close the ' +
                'other tabs and reload.'
            )
          );
        }, DB_OPEN_TIMEOUT_MS);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

function openDatabase(): Promise<IDBPDatabase<OfflineDb>> {
  return withOpenTimeout(
    openDB<OfflineDb>(DB_NAME, DB_VERSION, {
      /**
       * The upgrade is purely additive, which is what keeps it lossless: v2
       * adds the dead-letter store and three optional fields (`attempts`,
       * `lastError`, `nextAttemptAt`) to the existing entry shape. Entries
       * written by a v1 build stay exactly as they are and are read through
       * `attempts ?? 0`, so a user who queued writes on the old build and then
       * loaded this one still replays them in order. Nothing rewrites or
       * clears `pending-mutations`.
       */
      upgrade(db, oldVersion) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, {
            keyPath: 'id',
            autoIncrement: true,
          });
        }
        if (
          oldVersion < 2 &&
          !db.objectStoreNames.contains(FAILED_STORE_NAME)
        ) {
          db.createObjectStore(FAILED_STORE_NAME, {
            keyPath: 'id',
            autoIncrement: true,
          });
        }
      },
      /**
       * A tab still holding the v1 connection is stalling this upgrade. It
       * cannot be made to let go from here — a build older than the
       * `blocking()` handler below registers no `versionchange` listener at all
       * — so this only records why the open is about to time out.
       */
      blocked() {
        console.warn(
          'offlineMutationQueue: upgrade blocked by another open tab'
        );
      },
      /** This tab is the one stalling a newer tab — let go of the connection. */
      blocking() {
        const opened = dbPromise;
        dbPromise = null;
        void opened?.then((db) => {
          db.close();
        });
      },
    })
  );
}

function getDb(): Promise<IDBPDatabase<OfflineDb>> {
  if (!dbPromise) {
    // A rejected open is not cached: the stale tab that blocked it will close
    // eventually, and the next queue operation should get a fresh attempt
    // rather than the remembered failure.
    dbPromise = openDatabase().catch((error: unknown) => {
      dbPromise = null;
      throw error;
    });
  }
  return dbPromise;
}

/** IndexedDB-backed {@link OfflineQueueStorage} — the production backend. */
const indexedDbStorage: OfflineQueueStorage = {
  add: async (entry) => {
    const db = await getDb();
    const key = await db.add(STORE_NAME, entry);
    return key as number;
  },
  put: async (entry) => {
    const db = await getDb();
    await db.put(STORE_NAME, entry);
  },
  // No cast on the way out. `PendingMutation` is deliberately the permissive
  // read-back shape (see its doc comment), so what `idb` already infers from
  // the store's declared value type is the honest type — and the entry is
  // validated for real by `checkReplayable` at replay time, not here. The
  // previous `as PendingMutation[]` claimed a discriminated union the store
  // could not promise.
  getAll: async () => {
    const db = await getDb();
    return db.getAll(STORE_NAME);
  },
  count: async () => {
    const db = await getDb();
    return db.count(STORE_NAME);
  },
  remove: async (id) => {
    const db = await getDb();
    await db.delete(STORE_NAME, id);
  },
  clear: async () => {
    const db = await getDb();
    await db.clear(STORE_NAME);
  },
  addFailed: async (entry) => {
    const db = await getDb();
    await db.add(FAILED_STORE_NAME, entry);
  },
  getAllFailed: async () => {
    const db = await getDb();
    return db.getAll(FAILED_STORE_NAME);
  },
  clearFailed: async () => {
    const db = await getDb();
    await db.clear(FAILED_STORE_NAME);
  },
};

let storage: OfflineQueueStorage = indexedDbStorage;

/* -------------------------------------------------------------------------- */
/* Change notification                                                        */
/* -------------------------------------------------------------------------- */

const changeListeners = new Set<() => void>();

let cachedPendingCount = 0;

/**
 * Subscribe to any queue mutation — enqueue, remove, clear, dead-letter, or a
 * replay-state change.
 *
 * Shaped for `useSyncExternalStore`: pair it with
 * {@link getCachedPendingCount} or {@link isReplaying}, both of which are
 * synchronous reads of in-memory state.
 *
 * @returns Unsubscribe function.
 */
export function subscribeQueueChange(listener: () => void): () => void {
  changeListeners.add(listener);
  return () => {
    changeListeners.delete(listener);
  };
}

/** Notify every {@link subscribeQueueChange} listener, isolating failures. */
function notifyQueueChange(): void {
  for (const listener of changeListeners) {
    try {
      listener();
    } catch (error) {
      console.error('offlineMutationQueue: change listener failed', error);
    }
  }
}

/**
 * Last known number of entries in the live queue, maintained in memory.
 *
 * Cheap and synchronous, so it is safe as a `useSyncExternalStore` snapshot and
 * safe to read from the `online` event handler. It is refreshed from storage on
 * every {@link getPendingCount}, {@link getPendingMutations} and replay run.
 */
export function getCachedPendingCount(): number {
  return cachedPendingCount;
}

/** Update the cached count, notifying listeners only when it actually moved. */
function setCachedPendingCount(next: number): void {
  const clamped = Math.max(0, next);
  if (clamped === cachedPendingCount) return;
  cachedPendingCount = clamped;
  notifyQueueChange();
}

/* -------------------------------------------------------------------------- */
/* Replay phase                                                               */
/* -------------------------------------------------------------------------- */

/**
 * `'armed'` is the window between the `online` event and the replay run it is
 * about to trigger. It exists so {@link waitForReplayIdle} cannot report idle
 * during that window.
 */
type ReplayPhase = 'idle' | 'armed' | 'running';

let replayPhase: ReplayPhase = 'idle';
let armTimer: ReturnType<typeof setTimeout> | null = null;
let idleWaiters: Array<() => void> = [];

function setReplayPhase(next: ReplayPhase): void {
  if (replayPhase === next) return;
  replayPhase = next;

  if (next !== 'armed' && armTimer !== null) {
    clearTimeout(armTimer);
    armTimer = null;
  }

  if (next === 'idle') {
    const waiters = idleWaiters;
    idleWaiters = [];
    for (const resolve of waiters) {
      resolve();
    }
  }

  notifyQueueChange();
}

/** Enter the `'armed'` window, with a self-releasing timer behind it. */
function armReplay(): void {
  if (replayPhase !== 'idle') return;
  setReplayPhase('armed');
  armTimer = setTimeout(() => {
    armTimer = null;
    if (replayPhase === 'armed') {
      setReplayPhase('idle');
    }
  }, REPLAY_ARM_TIMEOUT_MS);
}

/**
 * Single-flight guard. `useOfflineQueue` is mounted once per consumer — the
 * Header badge is one of them — so without this every consumer would start its
 * own replay of the same entries on the same `online` event.
 *
 * @returns `false` when a run is already in flight and the caller must stand down.
 */
function beginReplayRun(): boolean {
  if (replayPhase === 'running') return false;
  setReplayPhase('running');
  return true;
}

/** True while a replay is armed or running. */
export function isReplaying(): boolean {
  return replayPhase !== 'idle';
}

/**
 * Resolves once no replay is armed or running. Resolves immediately when
 * already idle.
 */
export function waitForReplayIdle(): Promise<void> {
  if (replayPhase === 'idle') return Promise.resolve();
  return new Promise<void>((resolve) => {
    idleWaiters.push(resolve);
  });
}

/**
 * Arm on the `online` event itself, at module scope, before any React effect
 * runs. See the "online arming window" note in the module doc — this listener
 * is the whole reason `isReplaying()` is trustworthy from another `online`
 * handler.
 */
function handleOnline(): void {
  if (cachedPendingCount > 0) {
    armReplay();
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', handleOnline);
}

/* -------------------------------------------------------------------------- */
/* Write-failure classification                                               */
/* -------------------------------------------------------------------------- */

/**
 * Substrings that identify a transport failure when no HTTP status survived.
 *
 * Only consulted on the `status === undefined` path — a real answer is
 * classified by its status code, never by prose.
 */
const TRANSPORT_FAILURE_MARKERS: readonly string[] = [
  'fetcherror',
  'failed to fetch',
  'networkerror',
  'network request failed',
  'load failed',
  'timeout',
  'aborterror',
];

/**
 * Classify a failed Supabase write: did it never reach Postgres (TRANSIENT,
 * queue it) or did Postgres answer and refuse (PERMANENT, surface it)?
 *
 * Only the transient class may be enqueued. Queueing a refusal manufactures a
 * poison message: the replay re-issues it, Postgres refuses it again, and the
 * FIFO queue stops there for every other table forever.
 *
 * The signal is the HTTP status, not a thrown exception, because postgrest-js
 * never rejects — a fetch failure comes back as
 * `{ status: 0, error: { message: 'TypeError: Failed to fetch' } }` with an
 * empty `code`. So `status === 0` means "no response at all": a dead uplink,
 * a DNS failure, or a captive portal, which cannot MITM an HTTPS request and
 * so shows up as a refused handshake rather than as its own login page. `408`,
 * `429` and `5xx` are "the server did not process this write". Everything else
 * is the database saying no — `400` validation, `401`/`403` (RLS raises
 * `42501`, which PostgREST answers as `403`), `404`, `409`, a constraint
 * violation — and must reach the caller.
 *
 * A missing `status` (a mocked client, a shape the caller did not issue) is
 * classified from the message and otherwise treated as PERMANENT: the
 * conservative direction is to surface a failure, never to queue one.
 *
 * A 2xx that affected no rows never gets here at all. Every caller consults
 * this only when there is an error envelope, because `error === null` with an
 * empty body is an RLS-filtered write — permanent, and never queueable.
 *
 * This lives here rather than in the services because all three of them
 * (`supabaseTodoService`, `supabaseCalendarService`, `supabaseAlbumService`)
 * need the same decision on the write side and sit in three different features,
 * which `boundaries/element-types` in `eslint.config.js` forbids importing
 * across. They each carried a byte-identical private copy until this one
 * landed.
 */
export function isTransientWriteFailure(
  status: number | undefined,
  error: { readonly message?: string } | null | undefined
): boolean {
  if (status === 0) {
    return true;
  }
  if (status !== undefined) {
    return status === 408 || status === 429 || status >= 500;
  }
  const message = error?.message?.toLowerCase() ?? '';
  return TRANSPORT_FAILURE_MARKERS.some((marker) => message.includes(marker));
}

/* -------------------------------------------------------------------------- */
/* Queue operations                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Add a Supabase mutation to the offline queue.
 *
 * The only way in. It stamps the `kind` discriminator and the replay metadata
 * itself, so an entry this build wrote always satisfies
 * {@link checkReplayable}.
 *
 * Notifies {@link subscribeQueueChange} listeners so the pending badge and the
 * replay gate see the write immediately — the gate used to read a React state
 * count that nothing refreshed on enqueue, which left the queue undrained after
 * every reconnect.
 */
export async function enqueueSupabaseMutation(
  mutation: Omit<SupabasePendingMutation, keyof ReplayMetadata | 'kind'>
): Promise<number> {
  const entry: SupabasePendingMutation = {
    ...mutation,
    kind: 'supabase',
    timestamp: Date.now(),
    attempts: 0,
  };
  const key = await storage.add(entry);
  setCachedPendingCount(cachedPendingCount + 1);
  return key;
}

/**
 * Get all pending mutations in FIFO order.
 */
export async function getPendingMutations(): Promise<PendingMutation[]> {
  const entries = await storage.getAll();
  setCachedPendingCount(entries.length);
  return entries;
}

/**
 * Get the count of pending mutations, refreshing the cached count on the way.
 */
export async function getPendingCount(): Promise<number> {
  const count = await storage.count();
  setCachedPendingCount(count);
  return count;
}

/**
 * Remove a mutation from the queue after successful replay.
 */
export async function removeMutation(id: number): Promise<void> {
  await storage.remove(id);
  setCachedPendingCount(cachedPendingCount - 1);
}

/**
 * Clear all pending mutations, dead-lettered ones included.
 *
 * Called on sign-out: queued writes belong to the session that made them, and
 * leaving another member's failed writes behind on a shared family device would
 * replay them under the next member's session.
 */
export async function clearAllMutations(): Promise<void> {
  await storage.clear();
  await storage.clearFailed();
  setCachedPendingCount(0);
  notifyQueueChange();
}

/** Entries that will never be replayed again, newest last. */
export async function getFailedMutations(): Promise<FailedMutation[]> {
  return storage.getAllFailed();
}

/** Discard the dead-letter store after the user has been shown its contents. */
export async function clearFailedMutations(): Promise<void> {
  await storage.clearFailed();
  notifyQueueChange();
}

/* -------------------------------------------------------------------------- */
/* Replay                                                                     */
/* -------------------------------------------------------------------------- */

/** Result of replaying a single entry. */
export type ReplayOutcome =
  | { readonly status: 'success' }
  /** Transient — wire failure, timeout, 5xx. Worth another attempt later. */
  | { readonly status: 'retryable'; readonly error: string }
  /** Terminal — RLS refusal, constraint violation, zero rows affected. */
  | { readonly status: 'permanent'; readonly error: string };

export type MutationReplayFn = (
  mutation: PendingMutation
) => Promise<ReplayOutcome>;

/** What one {@link replayMutations} run did. */
export interface ReplaySummary {
  /** Entries written to Postgres and deleted from the queue. */
  readonly synced: number;
  /** Entries still queued that this run deliberately did not attempt. */
  readonly deferred: number;
  /** Entries evicted to the dead-letter store. The caller must report these. */
  readonly failed: readonly FailedMutation[];
  /** ms until the earliest backed-off entry is due, or `null` if none is. */
  readonly retryDelayMs: number | null;
  /** `true` when a run was already in flight and this call did nothing. */
  readonly skipped: boolean;
}

/** Exponential backoff for the n-th failed attempt, capped. */
export function backoffDelayMs(attempts: number): number {
  const exponent = Math.max(0, attempts - 1);
  return Math.min(BACKOFF_BASE_MS * 2 ** exponent, BACKOFF_MAX_MS);
}

/** Run one replay step, folding a thrown error into a retryable outcome. */
async function runReplayStep(
  replayFn: MutationReplayFn,
  mutation: PendingMutation
): Promise<ReplayOutcome> {
  try {
    return await replayFn(mutation);
  } catch (error) {
    return {
      status: 'retryable',
      error: error instanceof Error ? error.message : 'Unknown replay error',
    };
  }
}

/** Move an entry out of the live queue into the dead-letter store. */
async function deadLetterMutation(
  entry: PendingMutation,
  reason: string
): Promise<FailedMutation> {
  const record: FailedMutation = {
    failedAt: Date.now(),
    reason,
    mutation: entry,
  };
  await storage.addFailed(record);
  if (entry.id !== undefined) {
    await removeMutation(entry.id);
  }
  return record;
}

/**
 * Replay queued mutations in FIFO order.
 *
 * Single-flight: a second concurrent call returns `{ skipped: true }` rather
 * than replaying the same entries twice. Safe to call unconditionally — it
 * no-ops on an empty queue, which is what lets the `online` transition drop the
 * pending-count precondition that used to gate it.
 *
 * @param replayFn - Replays a single mutation and classifies the result.
 * @returns What the run did, including every dead-lettered entry.
 */
export async function replayMutations(
  replayFn: MutationReplayFn
): Promise<ReplaySummary> {
  if (!beginReplayRun()) {
    return {
      synced: 0,
      deferred: 0,
      failed: [],
      retryDelayMs: null,
      skipped: true,
    };
  }

  let synced = 0;
  let deferred = 0;
  let retryDelayMs: number | null = null;
  const failed: FailedMutation[] = [];

  try {
    const entries = await storage.getAll();
    setCachedPendingCount(entries.length);

    for (let index = 0; index < entries.length; index += 1) {
      const entry = entries[index];
      // `id` is assigned by the autoIncrement keyPath, so this only narrows.
      if (entry === undefined || entry.id === undefined) continue;

      if (!navigator.onLine) {
        deferred = entries.length - index;
        break;
      }

      const dueAt = entry.nextAttemptAt;
      const waitMs = dueAt === undefined ? 0 : dueAt - Date.now();
      if (waitMs > 0) {
        // Backoff, not failure: the entry keeps its place and the entries
        // behind it are not allowed to overtake it.
        deferred = entries.length - index;
        retryDelayMs = waitMs;
        break;
      }

      const outcome = await runReplayStep(replayFn, entry);

      if (outcome.status === 'success') {
        await removeMutation(entry.id);
        synced += 1;
        continue;
      }

      const attempts = (entry.attempts ?? 0) + 1;
      // A transient failure has to exhaust the attempt cap AND the wall clock.
      // The cap alone measures about 76 seconds of backoff, which is shorter
      // than an ordinary mobile reconnection — see {@link MAX_REPLAY_AGE_MS}.
      const exhausted =
        attempts >= MAX_REPLAY_ATTEMPTS &&
        Date.now() - entry.timestamp >= MAX_REPLAY_AGE_MS;

      if (outcome.status === 'permanent' || exhausted) {
        const reason =
          outcome.status === 'permanent'
            ? outcome.error
            : `${outcome.error} (giving up after ${attempts} attempts)`;
        // Poison message: out of the live queue so ordering is not blocked
        // forever, into the dead-letter store so nothing is silently dropped.
        failed.push(await deadLetterMutation(entry, reason));
        continue;
      }

      const delay = backoffDelayMs(attempts);
      await storage.put({
        ...entry,
        attempts,
        lastError: outcome.error,
        nextAttemptAt: Date.now() + delay,
      });
      notifyQueueChange();
      deferred = entries.length - index;
      retryDelayMs = delay;
      break;
    }
  } finally {
    try {
      setCachedPendingCount(await storage.count());
    } catch {
      // Keep the tracked count; a failed re-count must not strand the phase.
    }
    setReplayPhase('idle');
  }

  return { synced, deferred, failed, retryDelayMs, skipped: false };
}

/* -------------------------------------------------------------------------- */
/* Test seam                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Swap the persistence backend and reset every piece of in-memory queue state
 * (cached count, replay phase, pending idle waiters, arming timer).
 *
 * Exists for tests: the vitest environment is happy-dom, which has no
 * IndexedDB, so the replay logic is exercised against an in-memory
 * {@link OfflineQueueStorage}. Pass `null` to restore the IndexedDB backend.
 *
 * Registered {@link subscribeQueueChange} listeners are left alone — a caller
 * owns its own unsubscribe.
 */
export function setQueueStorage(next: OfflineQueueStorage | null): void {
  storage = next ?? indexedDbStorage;
  cachedPendingCount = 0;
  if (armTimer !== null) {
    clearTimeout(armTimer);
    armTimer = null;
  }
  replayPhase = 'idle';
  const waiters = idleWaiters;
  idleWaiters = [];
  for (const resolve of waiters) {
    resolve();
  }
}
